import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';

import { env } from 'env';
import { useStyles, reportSelectIconSx } from './styles';
import {
  useReportIt,
  ReportFormValues,
} from '@services/reportIt/useReportIt';
import {
  ReportIncidentType,
  ReportAttachmentFile,
  ReportAttachmentKind,
  ReportAttachmentPolicy,
  ReportAttachmentSource,
  ReportSymptomHint,
  ResolvedAttachmentLimits,
  getReportPolicy,
  kindForContentType,
  resolveAttachmentLimits,
} from '@services/reportIt/reportItApi';
import { computeAttachmentParts } from '@services/reportIt/attachmentParts';
import { normalizeScreenshotFile } from '@services/reportIt/imageNormalize';
import { useReportItContext } from './useReportItContext';

const INCIDENT_OPTIONS: Array<{ value: ReportIncidentType; labelKey: string }> = [
  { value: 'black_screen', labelKey: 'reportIssue.incidentTypes.blackScreen' },
  { value: 'audio_only', labelKey: 'reportIssue.incidentTypes.audioOnly' },
  { value: 'frozen_video', labelKey: 'reportIssue.incidentTypes.frozenVideo' },
  { value: 'disconnect', labelKey: 'reportIssue.incidentTypes.disconnected' },
  { value: 'other', labelKey: 'reportIssue.incidentTypes.other' },
];

// Symptom hints are no longer hand-picked in the UI (the redesigned modal asks
// only for a report type + free-text description). We still derive a sensible
// default set per incident type so the backend analysis keeps its starting
// signal.
const DEFAULT_HINTS_BY_INCIDENT: Partial<
  Record<ReportIncidentType, ReportSymptomHint[]>
> = {
  black_screen: ['screen_black', 'audio_still_playing'],
  audio_only: ['audio_still_playing', 'screen_black'],
  frozen_video: ['video_frozen'],
  disconnect: ['disconnected'],
};

const KIND_LABEL_KEYS: Record<ReportAttachmentKind, string> = {
  screenshot: 'reportIssue.kinds.screenshot',
  screen_recording: 'reportIssue.kinds.screenRecording',
  log_file: 'reportIssue.kinds.logFile',
};

// Browsers hand back an empty MIME type for several of the accepted files —
// .log most of all — and the backend derives the attachment kind from the
// declared type, so fall back to the extension instead of declaring nothing.
// Images are absent on purpose: normalisation assigns their type.
const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  log: 'text/plain',
  txt: 'text/plain',
  json: 'application/json',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
};

/** How far the policy fetch has got — attachments cannot be offered before it. */
type AttachmentPolicyStatus = 'loading' | 'ready' | 'unavailable';

/** An attachment while it is still in the dialog, before the report is sent. */
interface DraftAttachment extends ReportAttachmentFile {
  /** Stable across list edits, so an in-flight prepare updates the right row. */
  id: string;
  preparing: boolean;
  prepareFailed?: boolean;
}

// Opt-in feature flag for the Report It entry in the profile dropdown — matches
// the repo convention (every other REACT_APP_FEATURE_* gate is `=== 'true'`).
// Hidden unless the env explicitly enables it, so it can be shown/hidden per env.
export const reportIssueFeatureEnabled = (): boolean =>
  env.REACT_APP_FEATURE_REPORT_IT === 'true';

const defaultHints = (
  incidentType: ReportIncidentType | '',
): ReportSymptomHint[] =>
  incidentType ? DEFAULT_HINTS_BY_INCIDENT[incidentType] ?? [] : [];

const cleanDescription = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const extensionOf = (filename: string): string => {
  const dot = filename.lastIndexOf('.');

  return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : '';
};

// The file picker filters on both MIME types and extensions: a type-only accept
// list hides .log files, which most browsers report without a type at all.
const acceptFromLimits = (limits: ResolvedAttachmentLimits): string => {
  const kinds = Object.values(limits.byKind);
  const patterns = [
    ...kinds.flatMap((kind) => kind.contentTypes),
    ...kinds.flatMap((kind) => kind.extensions.map((ext) => `.${ext}`)),
  ];

  return Array.from(new Set(patterns)).join(',');
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const kb = bytes / 1024;

  if (kb < 1024) {
    return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  }

  const mb = kb / 1024;

  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
};

const megabytes = (bytes: number): number => Math.round(bytes / (1024 * 1024));

// Object-URL previews are best-effort: jsdom (tests) and locked-down embeds may
// not expose createObjectURL, in which case we simply skip the thumbnail.
const safeObjectUrl = (file: File): string | undefined => {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return undefined;
  }

  try {
    return URL.createObjectURL(file);
  } catch {
    return undefined;
  }
};

// Custom Select chevron — a downward arrow inside a thin circle (matches the
// MV-1077 design). MUI passes the positioning className through.
const ReportSelectIcon = ({ className }: { className?: string }) => (
  <Box className={className} sx={reportSelectIconSx}>
    <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
  </Box>
);

const AttachmentKindIcon = ({ kind }: { kind: ReportAttachmentKind }) => {
  if (kind === 'screen_recording') {
    return <VideocamOutlinedIcon fontSize="small" />;
  }

  if (kind === 'log_file') {
    return <DescriptionOutlinedIcon fontSize="small" />;
  }

  return <InsertDriveFileOutlinedIcon fontSize="small" />;
};

export interface ReportItDialogProps {
  open: boolean;
  onClose: () => void;
}

const ReportItDialog = ({ open, onClose }: ReportItDialogProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation('common');
  const { context, hasLiveExperience, failureReason } = useReportItContext();

  const [incidentType, setIncidentType] = useState<ReportIncidentType | ''>('');
  const [description, setDescription] = useState('');
  const [symptomHints, setSymptomHints] = useState<ReportSymptomHint[]>([]);
  const [attachments, setAttachments] = useState<DraftAttachment[]>([]);
  const [attachmentPolicy, setAttachmentPolicy] =
    useState<ReportAttachmentPolicy | undefined>(undefined);
  const [policyStatus, setPolicyStatus] =
    useState<AttachmentPolicyStatus>('loading');
  const [policyAttempt, setPolicyAttempt] = useState(0);
  const [attachmentError, setAttachmentError] = useState<string | undefined>();
  const [isProcessingAttachment, setIsProcessingAttachment] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const closeActionRef = useRef<HTMLButtonElement | null>(null);
  const prevOpenRef = useRef(false);
  const draftIdRef = useRef(0);

  const reportIt = useReportIt(context);
  const isSubmitting = reportIt.status === 'submitting';
  const isSuccess = reportIt.status === 'success';

  const resetForm = useCallback(() => {
    setIncidentType('');
    setDescription('');
    setSymptomHints([]);
    setAttachments([]);
    setAttachmentError(undefined);
    reportIt.reset();
  }, [reportIt]);

  // Reset form (and optionally prefill for event-screen failure reports) only
  // on the false → true transition so that mid-session state changes (e.g.
  // upload-progress rerenders) do not wipe the form while it is open.
  useEffect(() => {
    const didOpen = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (!didOpen) {
      return;
    }

    resetForm();

    if (!hasLiveExperience && failureReason) {
      setIncidentType('disconnect');
      setSymptomHints(defaultHints('disconnect'));
      // failureReason is the i18n key of the notification shown when the
      // experience failed — its translation is already a complete, localized
      // sentence, so it makes a good editable report description. translate()
      // returns the input unchanged if it isn't a known key, so this is safe.
      setDescription(translate(failureReason));
    }
  }, [open, resetForm, hasLiveExperience, failureReason, translate]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleIncidentChange = useCallback(
    (value: ReportIncidentType) => {
      setIncidentType(value);
      setSymptomHints(defaultHints(value));
      reportIt.reset();
    },
    [reportIt],
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;

    setPolicyStatus('loading');

    getReportPolicy()
      .then((policy) => {
        if (cancelled) {
          return;
        }

        setAttachmentPolicy(policy.attachmentPolicy);
        // A backend that enables attachments but omits a limit cannot be
        // rendered from policy, and the UI must not substitute its own caps.
        const usable =
          !policy.attachmentPolicy.attachmentsEnabled ||
          !!resolveAttachmentLimits(policy.attachmentPolicy);
        setPolicyStatus(usable ? 'ready' : 'unavailable');
      })
      .catch(() => {
        if (!cancelled) {
          setAttachmentPolicy(undefined);
          setPolicyStatus('unavailable');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, policyAttempt]);

  const limits = useMemo(
    () => resolveAttachmentLimits(attachmentPolicy),
    [attachmentPolicy],
  );

  const attachmentsEnabled =
    policyStatus === 'ready' && !!attachmentPolicy?.attachmentsEnabled && !!limits;

  // Thumbnail previews — one object URL per image attachment, revoked when the
  // set changes or the dialog unmounts so we don't leak blob URLs. Recordings
  // and log files get an icon instead and never reach an <img>.
  const previews = useMemo(
    () =>
      attachments.map((attachment) =>
        attachment.kind === 'screenshot'
          ? safeObjectUrl(attachment.file)
          : undefined,
      ),
    [attachments],
  );

  useEffect(
    () => () => {
      previews.forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    },
    [previews],
  );

  // On success, move focus to the close action so keyboard / screen-reader
  // users land on the actionable control instead of a now-removed form field.
  useEffect(() => {
    if (isSuccess) {
      closeActionRef.current?.focus();
    }
  }, [isSuccess]);

  // Only files with a complete part plan can be declared — the backend signs one
  // upload URL per part, so a file still being hashed has nothing to sign.
  const submittable = useMemo(
    () =>
      attachments.filter(
        (attachment) => !attachment.preparing && !attachment.prepareFailed,
      ),
    [attachments],
  );

  // Declaration order is what the backend keys its upload attempts and the hook
  // keys its progress by, so a preparing or failed file must not shift it.
  const declarationIndexes = useMemo(() => {
    const indexes = new Map<string, number>();

    submittable.forEach((attachment, index) => indexes.set(attachment.id, index));

    return indexes;
  }, [submittable]);

  const isPreparing = attachments.some((attachment) => attachment.preparing);

  const prepareAttachment = useCallback(
    async (draft: DraftAttachment, partBytes: number): Promise<void> => {
      try {
        const parts = await computeAttachmentParts(draft.file, partBytes);

        setAttachments((prev) =>
          prev.map((attachment) =>
            attachment.id === draft.id
              ? { ...attachment, parts, preparing: false }
              : attachment,
          ),
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Attachment preparation failed';
        // eslint-disable-next-line no-console
        console.error(`Failed to prepare Report It attachment: ${message}`, {
          eventId: context.eventId,
        });

        setAttachments((prev) =>
          prev.map((attachment) =>
            attachment.id === draft.id
              ? { ...attachment, preparing: false, prepareFailed: true }
              : attachment,
          ),
        );
        setAttachmentError(
          translate('reportIssue.errors.prepareFailed', {
            filename: draft.filename,
          }),
        );
      }
    },
    [context.eventId, translate],
  );

  const addFiles = useCallback(
    async (files: File[], source: ReportAttachmentSource): Promise<void> => {
      setAttachmentError(undefined);

      if (files.length === 0 || !limits) {
        return;
      }

      // Normalisation can take a moment (HEIC transcode), so flag a processing
      // state that drives the attach-button spinner. Part hashing that follows
      // is deliberately not awaited here — it reports itself per file.
      setIsProcessingAttachment(true);

      const added: DraftAttachment[] = [];

      try {
        const next = [...attachments];
        let totalBytes = next.reduce(
          (sum, attachment) => sum + attachment.byteSize,
          0,
        );

        for (const file of files) {
          if (next.length >= limits.maxAttachments) {
            setAttachmentError(
              translate('reportIssue.errors.maxAttachments', {
                count: limits.maxAttachments,
              }),
            );
            break;
          }

          let candidate = file;
          let contentType =
            file.type || CONTENT_TYPE_BY_EXTENSION[extensionOf(file.name)] || '';

          if (contentType === '' || contentType.startsWith('image/')) {
            // Images only: mobile pickers — iOS especially — hand back HEIC or a
            // blank MIME type, which is transcoded to JPEG so it passes the
            // backend allowlist. Recording and log bytes are never rewritten.
            // eslint-disable-next-line no-await-in-loop
            const normalized = await normalizeScreenshotFile(file);

            if (normalized.file) {
              candidate = normalized.file;
              contentType = normalized.file.type;
            }
          }

          const kind = kindForContentType(contentType, limits);

          if (!kind) {
            setAttachmentError(
              translate('reportIssue.errors.unsupportedType', {
                filename: file.name,
              }),
            );
            continue;
          }

          const kindPolicy = limits.byKind[kind];
          const ofKind = next.filter(
            (attachment) => attachment.kind === kind,
          ).length;

          if (ofKind >= kindPolicy.maxFiles) {
            setAttachmentError(
              translate('reportIssue.errors.maxOfKind', {
                count: kindPolicy.maxFiles,
                kind: translate(KIND_LABEL_KEYS[kind]),
              }),
            );
            continue;
          }

          if (candidate.size > kindPolicy.maxBytes) {
            setAttachmentError(
              translate('reportIssue.errors.tooLarge', {
                filename: file.name,
                maxMb: megabytes(kindPolicy.maxBytes),
              }),
            );
            continue;
          }

          if (totalBytes + candidate.size > limits.maxTotalBytes) {
            setAttachmentError(
              translate('reportIssue.errors.totalTooLarge', {
                totalMb: megabytes(limits.maxTotalBytes),
              }),
            );
            continue;
          }

          draftIdRef.current += 1;

          const draft: DraftAttachment = {
            id: `attachment-${draftIdRef.current}`,
            file: candidate,
            filename: candidate.name,
            contentType,
            byteSize: candidate.size,
            source,
            kind,
            parts: [],
            preparing: true,
          };

          totalBytes += candidate.size;
          next.push(draft);
          added.push(draft);
        }

        setAttachments(next);
      } finally {
        setIsProcessingAttachment(false);
      }

      added.forEach((draft) => {
        void prepareAttachment(draft, limits.multipartPartBytes);
      });
    },
    [attachments, limits, prepareAttachment, translate],
  );

  const handleAttachmentSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(event.target.files ?? []);
      event.target.value = '';
      void addFiles(selected, 'manual_upload');
    },
    [addFiles],
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      if (!isSubmitting && attachmentsEnabled) {
        setIsDragging(true);
      }
    },
    [isSubmitting, attachmentsEnabled],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      // Ignore leave events fired when moving onto a child element.
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setIsDragging(false);
      }
    },
    [],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);

      if (isSubmitting || !attachmentsEnabled) {
        return;
      }

      // Dropped files are not pre-filtered: per-file validation names the
      // offending file, which a silent drop cannot.
      void addFiles(Array.from(event.dataTransfer?.files ?? []), 'drag_drop');
    },
    [addFiles, isSubmitting, attachmentsEnabled],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      if (!attachmentsEnabled || isSubmitting) {
        return;
      }

      const pasted = Array.from(event.clipboardData?.items ?? [])
        .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter((file): file is File => !!file);

      if (pasted.length === 0) {
        // Not an image paste — let normal text paste proceed (e.g. description).
        return;
      }

      event.preventDefault();
      void addFiles(pasted, 'paste');
    },
    [addFiles, attachmentsEnabled, isSubmitting],
  );

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.id !== id));
    setAttachmentError(undefined);
  }, []);

  const handleRetryPolicy = useCallback(() => {
    setPolicyAttempt((attempt) => attempt + 1);
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      // No explicit selection maps to "other" so a report is never blocked on
      // picking a type (the free-text description carries the detail).
      const resolvedIncident: ReportIncidentType = incidentType || 'other';

      const values: ReportFormValues = {
        incidentType: resolvedIncident,
        description: cleanDescription(description),
        symptomHints,
        severityHint:
          resolvedIncident === 'black_screen' || resolvedIncident === 'disconnect'
            ? 'high'
            : undefined,
        // Session diagnostics (the client snapshot + session entities) are
        // always attached — not user-optional. The flag is kept for the API
        // contract; the backend currently treats it as informational.
        attachDiagnostics: true,
        attachments: attachmentsEnabled ? submittable : undefined,
      };

      await reportIt.submit(values);
    },
    [
      incidentType,
      description,
      symptomHints,
      attachmentsEnabled,
      submittable,
      reportIt,
    ],
  );

  const stopGameInputPropagation = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  const attachmentName = (
    attachment: DraftAttachment,
    index: number,
  ): string =>
    attachment.filename ||
    translate('reportIssue.attachmentIndexedName', { index: index + 1 });

  const failedAttachments = reportIt.failedAttachmentIndexes
    .map((index) => ({ index, attachment: submittable[index] }))
    .filter((entry) => !!entry.attachment);

  const renderRetryButton = (declarationIndex: number, name: string) => (
    <IconButton
      aria-label={translate('reportIssue.retryAttachment', { filename: name })}
      size="small"
      onClick={() => void reportIt.retryAttachment(declarationIndex)}
      disabled={reportIt.retryingAttachmentIndexes.includes(declarationIndex)}
      sx={classes.iconButton}
    >
      <RefreshIcon fontSize="small" />
    </IconButton>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{ sx: classes.dialogPaper }}
      BackdropProps={{ sx: classes.backdrop }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={isDragging ? classes.formDragging : classes.form}
        data-testid="report-issue-dropzone"
        onKeyDown={stopGameInputPropagation}
        onKeyUp={stopGameInputPropagation}
        onPointerDown={stopGameInputPropagation}
        onPointerUp={stopGameInputPropagation}
        onMouseDown={stopGameInputPropagation}
        onMouseUp={stopGameInputPropagation}
        onPaste={handlePaste}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <IconButton
          ref={closeActionRef}
          aria-label={translate('reportIssue.close')}
          onClick={handleClose}
          disabled={isSubmitting}
          size="small"
          data-testid="report-issue-close-action"
          sx={classes.closeButton}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {isSuccess ? (
          <Box sx={classes.successContent}>
            <Typography component="h2" sx={classes.thankYouHeading}>
              {translate('reportIssue.thankYou')}
            </Typography>
            <Alert severity="success" icon={false} sx={classes.successAlert}>
              {translate('reportIssue.success', {
                reportId: reportIt.result?.reportId,
              })}
            </Alert>
            {reportIt.attachmentUploadFailed ? (
              <Alert severity="warning" sx={classes.attachmentWarning}>
                {translate('reportIssue.attachmentUploadWarning')}
              </Alert>
            ) : null}
            {/* A failed upload must not lose the file: keep it listed with its
                own retry, which takes a fresh owner-scoped upload attempt. */}
            {failedAttachments.length > 0 ? (
              <Box sx={classes.attachmentList} data-testid="report-issue-failed-list">
                {failedAttachments.map(({ index, attachment }) => (
                  <Box key={attachment.id} sx={classes.attachmentItem}>
                    <Box sx={classes.attachmentInfo}>
                      <Typography variant="body2" noWrap>
                        {attachmentName(attachment, index)}
                      </Typography>
                      <Typography variant="caption" sx={classes.attachmentMeta}>
                        {reportIt.retryingAttachmentIndexes.includes(index)
                          ? translate('reportIssue.uploading')
                          : translate('reportIssue.attachmentUploadFailedItem')}
                      </Typography>
                    </Box>
                    {renderRetryButton(index, attachmentName(attachment, index))}
                  </Box>
                ))}
              </Box>
            ) : null}
            <Typography variant="body2" sx={classes.successDetail}>
              {translate('reportIssue.successDetail')}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography component="h2" sx={classes.title}>
              {translate('reportIssue.title')}
            </Typography>

            <DialogContent sx={classes.dialogContent}>
              {reportIt.status === 'error' ? (
                <Alert severity="error">
                  {reportIt.error || translate('reportIssue.errors.submitFailed')}
                </Alert>
              ) : null}

              <Box sx={classes.topRow}>
                <TextField
                  select
                  value={incidentType}
                  onChange={(event) =>
                    handleIncidentChange(event.target.value as ReportIncidentType)
                  }
                  disabled={isSubmitting}
                  sx={classes.selectField}
                  SelectProps={{
                    displayEmpty: true,
                    IconComponent: ReportSelectIcon,
                    renderValue: (selected) => {
                      const value = selected as ReportIncidentType | '';

                      if (!value) {
                        return (
                          <Box component="span" sx={classes.selectPlaceholder}>
                            {translate('reportIssue.selectTypePlaceholder')}
                          </Box>
                        );
                      }

                      const option = INCIDENT_OPTIONS.find(
                        (item) => item.value === value,
                      );

                      return translate(
                        option?.labelKey ?? 'reportIssue.incidentTypes.other',
                      );
                    },
                    SelectDisplayProps: {
                      'aria-label': translate('reportIssue.issueType'),
                    },
                    MenuProps: { PaperProps: { sx: classes.menuPaper } },
                  }}
                >
                  {INCIDENT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {translate(option.labelKey)}
                    </MenuItem>
                  ))}
                </TextField>

                {policyStatus === 'loading' || attachmentsEnabled ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={limits ? acceptFromLimits(limits) : undefined}
                      multiple
                      hidden
                      onChange={handleAttachmentSelect}
                      data-testid="report-issue-attachments-input"
                    />
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={
                        isSubmitting ||
                        isProcessingAttachment ||
                        policyStatus === 'loading' ||
                        !limits ||
                        attachments.length >= limits.maxAttachments
                      }
                      endIcon={
                        isProcessingAttachment || policyStatus === 'loading' ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <FileUploadOutlinedIcon />
                        )
                      }
                      sx={classes.attachField}
                    >
                      <Box component="span" sx={classes.attachLabel}>
                        {translate(
                          policyStatus === 'loading'
                            ? 'reportIssue.attachmentsLoading'
                            : 'reportIssue.attachFile',
                        )}
                      </Box>
                    </Button>
                  </>
                ) : null}
              </Box>

              <TextField
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                disabled={isSubmitting}
                fullWidth
                multiline
                minRows={5}
                placeholder={translate('reportIssue.descriptionPlaceholder')}
                sx={classes.textArea}
                inputProps={{
                  maxLength: 2000,
                  'aria-label': translate('reportIssue.descriptionLabel'),
                }}
              />

              {policyStatus === 'unavailable' ? (
                <Alert
                  severity="info"
                  sx={classes.attachmentWarning}
                  data-testid="report-issue-attachments-unavailable"
                  action={
                    <Button
                      type="button"
                      color="inherit"
                      size="small"
                      onClick={handleRetryPolicy}
                    >
                      {translate('reportIssue.retryPolicy')}
                    </Button>
                  }
                >
                  {translate('reportIssue.attachmentsUnavailable')}
                </Alert>
              ) : null}

              {attachmentError ? (
                <Alert severity="warning" sx={classes.attachmentWarning}>
                  {attachmentError}
                </Alert>
              ) : null}

              {attachmentsEnabled && limits ? (
                <Typography variant="caption" sx={classes.attachmentMeta}>
                  {translate('reportIssue.attachmentLimits', {
                    count: limits.maxAttachments,
                    totalMb: megabytes(limits.maxTotalBytes),
                  })}
                </Typography>
              ) : null}

              {attachments.length > 0 ? (
                <Box sx={classes.attachmentList}>
                  {attachments.map((attachment, index) => {
                    const declarationIndex = declarationIndexes.get(attachment.id);
                    const name = attachmentName(attachment, index);
                    const hasFailed =
                      declarationIndex !== undefined &&
                      reportIt.failedAttachmentIndexes.includes(declarationIndex);

                    return (
                      <Box key={attachment.id} sx={classes.attachmentItem}>
                        {previews[index] ? (
                          <Box
                            component="img"
                            src={previews[index]}
                            alt={name}
                            sx={classes.attachmentThumbnail}
                          />
                        ) : (
                          <Box sx={classes.attachmentFileIcon}>
                            <AttachmentKindIcon kind={attachment.kind} />
                          </Box>
                        )}
                        <Box sx={classes.attachmentInfo}>
                          <Typography variant="body2" noWrap>
                            {name}
                          </Typography>
                          <Typography variant="caption" sx={classes.attachmentMeta}>
                            {attachment.preparing
                              ? translate('reportIssue.preparing')
                              : formatBytes(attachment.byteSize)}
                          </Typography>
                          {reportIt.phase === 'uploading' &&
                          declarationIndex !== undefined ? (
                            <LinearProgress
                              variant="determinate"
                              value={Math.round(
                                (reportIt.uploadProgress[declarationIndex] ?? 0) *
                                  100,
                              )}
                              sx={classes.uploadProgress}
                            />
                          ) : null}
                        </Box>
                        {hasFailed && declarationIndex !== undefined
                          ? renderRetryButton(declarationIndex, name)
                          : null}
                        <IconButton
                          aria-label={translate('reportIssue.removeAttachment', {
                            filename: name,
                          })}
                          size="small"
                          onClick={() => handleRemoveAttachment(attachment.id)}
                          disabled={isSubmitting}
                          sx={classes.iconButton}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              ) : null}
            </DialogContent>

            <DialogActions sx={classes.actions}>
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
                sx={classes.cancelButton}
              >
                {translate('reportIssue.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || isProcessingAttachment || isPreparing}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : undefined
                }
                data-testid="report-issue-submit"
                sx={classes.submitButton}
              >
                {isSubmitting
                  ? translate(
                      reportIt.phase === 'uploading'
                        ? 'reportIssue.uploading'
                        : 'reportIssue.sending',
                    )
                  : translate('reportIssue.submit')}
              </Button>
            </DialogActions>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default ReportItDialog;
